import styled from 'styled-components';
import { Badge } from '@shared/ui/Badge';
import { useGetKadrySearchCostQuery } from '@shared/api';
import {
  kadryCurrentPackage,
  kadryDate,
  kadryDateTime,
  kadryPlural,
  type KadryQuotaPackage,
} from '@entities/kadry';
import { KadryMuted } from '../ui';
import { Line, Step } from './connectionsShared';

const PackageList = styled.ul`
  margin: ${({ theme }) => theme.spacing(1)} 0 0;
  padding-left: ${({ theme }) => theme.spacing(5)};
  font-size: 13px;
  line-height: 1.6;
`;

const num = new Intl.NumberFormat('ru-RU');

export interface QuotaJson {
  resource?: string;
  limit?: number | null;
  remaining?: number | null;
  used?: number | null;
  packages?: KadryQuotaPackage[];
}

/** Остатки hh по последней сверке: пакеты запросов API и просмотры резюме за сутки. */
export function TariffsQuotasCard({ capabilities }: { capabilities: Record<string, unknown> }) {
  const { data: cost } = useGetKadrySearchCostQuery();
  const quotas = (capabilities.quotas as QuotaJson[] | undefined) ?? [];
  const api = quotas.find((q) => q.resource === 'hh.special_requests');
  const views = quotas.find((q) => q.resource === 'hh.manager_resume_views');
  const packages = api?.packages ?? [];
  if (!api && !views) return null;
  const cur = kadryCurrentPackage(packages);
  const need = cost?.hh_daily_need ?? 0;
  const daysLeft = (n: number) =>
    `≈${num.format(Math.floor(n / need))} ${kadryPlural(Math.floor(n / need), ['день', 'дня', 'дней'])}`;

  return (
    <Step>
      <KadryMuted>Остатки на hh.ru (сверка раз в час):</KadryMuted>
      {packages.length > 0 ? (
        <PackageList>
          {packages.map((p) => (
            <li key={p.id}>
              Пакет запросов API{p.expires_at ? ` до ${kadryDate(p.expires_at)}` : ''}: осталось{' '}
              <b>{num.format(p.remaining)}</b> из {num.format(p.limit)}
              {p.id === cur?.id && (
                <>
                  {' '}
                  <Badge $tone={need > 0 && p.remaining < need * 2 ? 'warning' : 'info'}>
                    {p.last_spent_at ? `списывается сейчас · ${kadryDateTime(p.last_spent_at)}` : 'списывается первым'}
                  </Badge>
                  {need > 0 && <> — поиску хватит на {daysLeft(p.remaining)}</>}
                </>
              )}
            </li>
          ))}
        </PackageList>
      ) : (
        api?.remaining != null && (
          <Line>
            Пакет запросов API: осталось {num.format(api.remaining)}
            {api.limit != null ? ` из ${num.format(api.limit)}` : ''}
          </Line>
        )
      )}
      {need > 0 && (
        <KadryMuted>
          Поиск по активным заявкам тратит до {num.format(need)} запросов в сутки. hh списывает их из пакета
          одного доступа к базе резюме; когда он кончится, должен перейти на следующий — если начнёт отказывать,
          ОСНОВА пришлёт уведомление.
        </KadryMuted>
      )}
      {views?.limit != null && (
        <Line>
          Просмотры резюме сегодня: {num.format(views.used ?? 0)} из {num.format(views.limit)}
        </Line>
      )}
    </Step>
  );
}

export { TariffsQuotasCard as HhQuotas };
